import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

// Animated "A + data bits" hero mark (mouse-reactive, gentle float).
export default function HeroMark({ className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const W = canvas.clientWidth;
    const H = canvas.clientHeight;
    canvas.width = W * DPR;
    canvas.height = H * DPR;
    ctx.scale(DPR, DPR);

    const cols = 40;
    const rows = 14;
    let t = 0;
    let raf;
    const mouse = { x: -9999, y: -9999 };
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    window.addEventListener("mousemove", onMove);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const sx = W / (cols - 1);
      const RAD = 90;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const nx = c / (cols - 1);
          const x = c * sx;
          const center = Math.exp(-Math.pow((nx - 0.5) * 3.0, 2));
          const wave = Math.sin(nx * 11 + t * 1.7 + r * 0.4) * 5 + Math.sin(nx * 22 - t * 2.2) * 2.5;
          const baseY = H * 0.62 + r * (H * 0.032);
          let y = baseY - center * (H * 0.5) + wave - r * 1.3;
          let boost = 0;
          const dx = x - mouse.x;
          const dy = y - mouse.y;
          const dist = Math.hypot(dx, dy);
          let drawX = x;
          if (dist < RAD) {
            const f = 1 - dist / RAD;
            const ang = Math.atan2(dy, dx);
            y += Math.sin(ang) * f * 18;
            drawX += Math.cos(ang) * f * 18;
            boost = f;
          }
          if (y > H + 5 || y < -5) continue;
          const R = Math.round(16 + (0 - 16) * nx);
          const G = Math.round(185 + (229 - 185) * nx);
          const B = Math.round(129 + (255 - 129) * nx);
          const alpha = Math.min(1, Math.max(0.06, 0.26 + center * 0.6 - r * 0.03 + boost * 0.6));
          ctx.beginPath();
          ctx.fillStyle = `rgba(${R},${G},${B},${alpha})`;
          ctx.arc(drawX, y, 0.8 + center * 1.2 + boost * 1.6, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      t += 0.016;
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return (
    <motion.div
      animate={{ y: [0, -12, 0] }}
      transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      className={`relative ${className}`}
      data-testid="hero-mark"
    >
      <div
        className="pointer-events-none absolute inset-0 blur-2xl"
        style={{ background: "radial-gradient(50% 45% at 50% 45%, rgba(0,229,255,0.28), transparent 70%)" }}
      />
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
      <div className="absolute left-1/2 top-[8%] h-[62%] -translate-x-1/2" style={{ aspectRatio: "1 / 1" }}>
        <svg viewBox="0 0 200 200" className="h-full w-full" preserveAspectRatio="xMidYMid meet">
          <g stroke="#ffffff" strokeWidth="3.4" fill="none" strokeLinecap="round" strokeLinejoin="round"
             style={{ filter: "drop-shadow(0 0 10px rgba(0,229,255,0.5))" }}>
            <path d="M100 34 L58 158" />
            <path d="M100 34 L142 158" />
            <path d="M100 58 L74 142" />
            <path d="M100 58 L126 142" />
          </g>
        </svg>
      </div>
    </motion.div>
  );
}
